/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  DrawRuleBasis,
  DrawRuleCpCcDto,
  DrawRuleCpStageDto,
  DrawRuleDetailsDto,
  DrawRuleSaveDto,
  DrawRuleStageDto,
} from '@gh/builder-shared-data';
import {
  createArrayHelper,
  entityComplementor,
  EntityConfig,
  entityValidatorByEntityConfig,
  fieldStateByEntityConfig,
  fieldValidatorByEntityConfig,
  formStateSnapshot,
  FormStateSnapshot,
  getFormStateSnapshotValue,
  mergeAndValidateFields,
  NumberTypes,
  requiredValidator,
  setFormStateSnapshotValue,
} from '@gh/common-shared';
import { EMPTY_ID, ID, WithEntityState } from '@gh/common-shared-data';
import { FocusedCell } from '@gh/core-ui-grid';
import { isNotNil, zeroNumberAsUndefined } from '@gh/core-util';
import { first, get, isEmpty, isNil, keyBy, map, omit, pick, uniqueId } from 'lodash';

export type DrawRuleCpCc = DrawRuleCpCcDto & { isCriticalPathDeleted?: boolean; isCostCenterDeleted?: boolean };
export type DrawRuleCpStage = DrawRuleCpStageDto & {
  // criticalPathName field is duplicated just to ease access
  criticalPathName: string;
  sortOrder: number;
  isCriticalPathDeleted?: boolean;
};
export type DrawRuleCpStageOrCc = DrawRuleCpCc | DrawRuleCpStage;
export type DrawRuleStage = WithEntityState<{
  drawRuleStageId?: ID;
  cpStage?: DrawRuleCpStage;
  cpCostCenter?: DrawRuleCpCc;
  deletedCpStageOrCc?: DrawRuleCpStageOrCc;
  stageNumber: number;
  name: string;
  memo: string;
  isDepositStage: boolean;
  isDeductDeposit: boolean;
  jobBudgetPct?: number;
  amountDue?: number;
  pctDue?: number;
  isNew?: boolean;
  deletedCriticalPathName?: string;
  isCriticalPathDeleted: boolean;
  status?: number;
}>;

export type DrawRuleMasterInfo = {
  code: string;
  name: string;
  basis: DrawRuleBasis;
  isNotProceeding: boolean;
};

export type DrawRuleDetailsSelection = {
  stageId?: ID;
  focusedCell?: FocusedCell;
};

export type DrawRuleDetails = {
  drawRuleId: ID;
  masterInfo: FormStateSnapshot<DrawRuleMasterInfo>;
  stages: DrawRuleStage[];
  criticalPathCostCenters: DrawRuleCpCc[];
  criticalPathStages: DrawRuleCpStage[];
  selection: DrawRuleDetailsSelection;
};

export const DRAW_RULE_DETAILS_INIT: DrawRuleDetails = {
  drawRuleId: EMPTY_ID,
  masterInfo: formStateSnapshot({
    code: '',
    name: '',
    basis: DrawRuleBasis.CriticalPath,
    isNotProceeding: false,
  }),
  stages: [],
  criticalPathStages: [],
  criticalPathCostCenters: [],
  selection: {},
};

export const genDrawRuleCpStageCcId = (criticalPathId: ID, costCenterId: ID) => `${criticalPathId}:${costCenterId}`;

export const drawRuleDeletedCpStageOrCc = (dto: DrawRuleStageDto): Maybe<DrawRuleCpStageOrCc> => {
  const stageOrCc = pick(dto, ['costCenterCode', 'costCenterId', 'criticalPathId', 'criticalPathName']);
  return isEmpty(stageOrCc) ? void 0 : <DrawRuleCpStageOrCc>stageOrCc;
};

export const drawRuleDetailsToDto = (model: DrawRuleDetails): DrawRuleSaveDto => {
  const masterInfo = getFormStateSnapshotValue(model.masterInfo);

  return {
    drawRuleId: model.drawRuleId,
    code: masterInfo.code,
    name: masterInfo.name,
    basis: masterInfo.basis,
    isNotProceeding: masterInfo.isNotProceeding,
    stages: model.stages.map((stage) => {
      // take assigned CP stage/CP CC/deleted CP stage/deleted CP CC
      const cpStageOrCc = stage.cpStage || stage.cpCostCenter || stage.deletedCpStageOrCc;
      return {
        ...<DrawRuleStageDto>omit(
          stage,
          [
            'drawRuleStageId', 'cpStage', 'cpCostCenter', 'entityState', 'isNew', 'deletedCriticalPathName',
            'deletedCpStageOrCc',
          ]),
        drawRuleStageId: stage.isNew ? void 0 : stage.drawRuleStageId,
        criticalPathId: get(cpStageOrCc, 'criticalPathId'),
        criticalPathName: get(cpStageOrCc, 'criticalPathName'),
        costCenterId: get(cpStageOrCc, 'costCenterId'),
        costCenterCode: get(cpStageOrCc, 'costCenterCode'),
      };
    }),
  };
};

export const drawRuleDetailsForClone = (model: DrawRuleDetails): DrawRuleDetails => ({
  ...model,
  masterInfo: setFormStateSnapshotValue(model.masterInfo, (masterInfo) => ({ ...masterInfo, code: '', name: '' })),
  stages: model.stages.map((stage) => ({ ...stage, drawRuleStageId: uniqueId('draw-rule-stage:'), isNew: true })),
});

export const drawRuleStageFromDto = (dto: DrawRuleStageDto,
                                     cpStage?: DrawRuleCpStage,
                                     cpCostCenter?: DrawRuleCpCc): DrawRuleStage =>
  complementDrawRuleStage({
    ...<DrawRuleStage>omit(dto, 'criticalPathId', 'costCenterId', 'amountDue', 'pctDue'),
    // treat amountDue/pctDue as void 0
    amountDue: zeroNumberAsUndefined(dto.amountDue),
    pctDue: zeroNumberAsUndefined(dto.pctDue),
    cpStage,
    cpCostCenter,
    // if cpStage and cpCostCenter are nil then
    // we can still have assigned CP or CC if CP was marked as deleted or CC was deleted from its CP
    deletedCpStageOrCc: isNil(cpStage) && isNil(cpCostCenter) ? drawRuleDeletedCpStageOrCc(dto) : void 0,
    deletedCriticalPathName: dto.isCriticalPathDeleted ? dto.criticalPathName : void 0,
  });

export const drawRuleDetailsFromDto = (dto: DrawRuleDetailsDto): DrawRuleDetails => {
  const criticalPathStages = dto.criticalPathStages.map((stage, i) => ({
    ...stage,
    criticalPathName: stage.name,
    sortOrder: i + 1,
  }));
  const cpStageIndex = keyBy(criticalPathStages, 'criticalPathId');
  const cpCostCenterIndex = keyBy(
    dto.criticalPathCostCenters,
    (cc) => genDrawRuleCpStageCcId(cc.criticalPathId, cc.costCenterId));

  return {
    drawRuleId: dto.drawRule.drawRuleId,
    masterInfo: formStateSnapshot(pick(dto.drawRule, ['code', 'name', 'basis', 'isNotProceeding'])),
    stages: map(dto.drawRule.stages, (stage) => drawRuleStageFromDto(
      stage,
      // if CP was assigned => take its definition from the list of active CPs
      isNotNil(stage.criticalPathId) && isNil(stage.costCenterId)
        ? cpStageIndex[stage.criticalPathId]
        : void 0,
      // if CC was assigned => take its definition from the list of active CCs (by CP and CC ids)
      isNotNil(stage.criticalPathId) && isNotNil(stage.costCenterId)
        ? cpCostCenterIndex[genDrawRuleCpStageCcId(stage.criticalPathId, stage.costCenterId)]
        : void 0)),
    criticalPathCostCenters: dto.criticalPathCostCenters,
    criticalPathStages,
    selection: {
      stageId: get(first(dto.drawRule.stages), 'drawRuleStageId', void 0),
    },
  };
};

const DRAW_RULE_STAGE_PCT_AND_AMOUNT_FIELDS = ['amountDue', 'pctDue'];

export const isDrawRuleStagePctOrAmountField = (name: string) => DRAW_RULE_STAGE_PCT_AND_AMOUNT_FIELDS.includes(name);
export const isDrawRuleStageHasEmptyParagraph = (stage: DrawRuleStage) => isNotNil(requiredValidator(stage.memo));
export const hasDrawRuleStageCpDeleted = (stage: DrawRuleStage) => stage.isCriticalPathDeleted;

export const getDrawRuleStageId = (stage: DrawRuleStage) => stage.drawRuleStageId;

export const drawRuleStageArr = createArrayHelper(getDrawRuleStageId);

export const createDrawRuleStage = (stageNumber: number): DrawRuleStage => complementDrawRuleStage({
  drawRuleStageId: uniqueId('draw-rule-stage:'),
  stageNumber,
  name: '',
  memo: '',
  isDepositStage: false,
  isDeductDeposit: false,
  isNew: true,
  isCriticalPathDeleted: false,
  status: 1,
});

const DRAW_RULE_MASTER_INFO_ENTITY_CONFIG: EntityConfig<DrawRuleMasterInfo> = {
  code: { required: true },
  name: { required: true },
};

const DRAW_RULE_STAGE_ENTITY_CONFIG: EntityConfig<DrawRuleStage> = {
  name: { required: true },
  amountDue: {
    required: (stage) => !Boolean(stage.pctDue),
    editable: (stage) => !Boolean(stage.pctDue),
    type: NumberTypes.Price,
  },
  pctDue: {
    required: (stage) => !Boolean(stage.amountDue),
    editable: (stage) => !Boolean(stage.amountDue),
    type: NumberTypes.Percents,
  },
  cpStage: {
    validator: ({ cpStage }) => {
      if (cpStage && cpStage.isCriticalPathDeleted) {
        return { deleted: { messageId: 'msg_critical_path_deleted', deletedName: cpStage.criticalPathName } };
      }
    },
  },
  cpCostCenter: {
    validator: ({ cpCostCenter }) => {
      if (cpCostCenter && cpCostCenter.isCriticalPathDeleted) {
        return { deleted: { messageId: 'msg_critical_path_deleted', deletedName: cpCostCenter.criticalPathName } };
      } else if (cpCostCenter && cpCostCenter.isCostCenterDeleted) {
        return { deleted: { messageId: 'msg_critical_path_cc_deleted', ccCode: cpCostCenter.costCenterCode } };
      }
    },
  },
  jobBudgetPct: {
    type: NumberTypes.Percents,
  },
};

export const validateDrawRuleMasterInfo = entityValidatorByEntityConfig(DRAW_RULE_MASTER_INFO_ENTITY_CONFIG);

export const validateDrawRuleStage = entityValidatorByEntityConfig(DRAW_RULE_STAGE_ENTITY_CONFIG);
export const validateDrawRuleStageField = fieldValidatorByEntityConfig(DRAW_RULE_STAGE_ENTITY_CONFIG);
export const drawRuleStageFieldState = fieldStateByEntityConfig(DRAW_RULE_STAGE_ENTITY_CONFIG);
export const mergeDrawRuleStageFields = mergeAndValidateFields(validateDrawRuleStage);
export const complementDrawRuleStage = entityComplementor(validateDrawRuleStage);
